import { useState } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  FileText,
  Send,
  Download,
  Building2,
  User,
  Mail,
  DollarSign,
  Clock,
  Shield,
  TrendingUp,
  Loader2,
  CheckCircle,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function ExecutiveSummaryGenerator() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [generatedSummary, setGeneratedSummary] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    organization_name: "",
    contact_name: "",
    contact_email: "",
    vendors_verified: 100,
    hours_saved_monthly: 60,
    hourly_rate: 45,
    competitive_savings_percent: 12,
    annual_spend: 500000,
    risk_reduction_percent: 85,
    minority_spend_target: 15,
    notes: ""
  });

  const handleGenerate = async () => {
    if (!formData.organization_name || !formData.contact_name) {
      toast.error("Organization name and contact name are required");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/executive-summary/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeneratedSummary(data);
        toast.success("Executive Summary generated successfully!");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate summary");
      }
    } catch (err) {
      toast.error("Failed to generate summary");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!generatedSummary?.summary_id) return;
    if (!formData.contact_email) {
      toast.error("Contact email is required to send");
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(`${API}/executive-summary/${generatedSummary.summary_id}/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        toast.success(`Executive Summary sent to ${formData.contact_email}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (err) {
      toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const metrics = generatedSummary?.metrics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-amber-600" />
            Executive Summary Generator
          </h2>
          <p className="text-slate-500 mt-1">Generate Board-Ready procurement impact reports</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Prospect Information
            </CardTitle>
            <CardDescription>Enter details for the executive summary</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Organization Name *</Label>
                <Input
                  placeholder="Memorial Hospital System"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input
                  placeholder="Jane Smith, CSCO"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Contact Email (for sending)</Label>
              <Input
                type="email"
                placeholder="jsmith@memorial.org"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                ROI Metrics
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hours Saved (Monthly)</Label>
                  <Input
                    type="number"
                    value={formData.hours_saved_monthly}
                    onChange={(e) => setFormData({ ...formData, hours_saved_monthly: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Annual Spend ($)</Label>
                  <Input
                    type="number"
                    value={formData.annual_spend}
                    onChange={(e) => setFormData({ ...formData, annual_spend: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Competitive Savings (%)</Label>
                  <Input
                    type="number"
                    value={formData.competitive_savings_percent}
                    onChange={(e) => setFormData({ ...formData, competitive_savings_percent: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-500" />
                Compliance Metrics
              </h4>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Vendors Verified (%)</Label>
                  <Input
                    type="number"
                    value={formData.vendors_verified}
                    onChange={(e) => setFormData({ ...formData, vendors_verified: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Risk Reduction (%)</Label>
                  <Input
                    type="number"
                    value={formData.risk_reduction_percent}
                    onChange={(e) => setFormData({ ...formData, risk_reduction_percent: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Minority Spend Target (%)</Label>
                  <Input
                    type="number"
                    value={formData.minority_spend_target}
                    onChange={(e) => setFormData({ ...formData, minority_spend_target: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Additional Notes</Label>
              <Textarea
                placeholder="Any additional context for the summary..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Executive Summary
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Generated Summary Preview */}
        <Card className={generatedSummary ? "border-emerald-300 bg-emerald-50/30" : ""}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {generatedSummary ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <Eye className="w-5 h-5 text-slate-400" />
              )}
              {generatedSummary ? "Summary Generated" : "Preview"}
            </CardTitle>
            <CardDescription>
              {generatedSummary 
                ? `Generated for ${generatedSummary.organization_name}`
                : "Your executive summary will appear here"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {generatedSummary ? (
              <div className="space-y-4">
                {/* Metrics Summary */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <p className="text-xs text-red-600 font-medium">RISK REDUCTION</p>
                    <p className="text-2xl font-bold text-red-700">{metrics.risk_reduction_percent}%</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-emerald-600 font-medium">HOURS SAVED/YEAR</p>
                    <p className="text-2xl font-bold text-emerald-700">{metrics.annual_hours_saved}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium">LABOR SAVINGS</p>
                    <p className="text-2xl font-bold text-blue-700">${metrics.labor_savings?.toLocaleString()}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium">TOTAL ANNUAL VALUE</p>
                    <p className="text-2xl font-bold text-amber-700">${metrics.total_annual_value?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewOpen(true)}
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Full Preview
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={sending || !formData.contact_email}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send to {formData.contact_email ? formData.contact_email.split('@')[0] : 'Contact'}
                  </Button>
                </div>

                <p className="text-xs text-slate-500 text-center">
                  Summary ID: {generatedSummary.summary_id}
                </p>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Fill in the form and click generate</p>
                <p className="text-sm">Your Board-Ready summary will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Full Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Executive Summary Preview</DialogTitle>
          </DialogHeader>
          {generatedSummary?.html_content && (
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: generatedSummary.html_content }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
